/**
 * OfferDiceTryController — the local "try the dice" demo launched from an offer.
 *
 * Extends DiceTryScene (the shared isolated shoot scene). Adds an OfferDiceChooser
 * (top panel + palette) so the player can build a 3-die combo from the offer's
 * dice; it collapses to a round button while the dice are in hand / rolling and
 * re-expands on tap.
 */

import { DiceTryScene, type DiceTrySceneDeps } from '../dice-try/DiceTryScene';
import { OfferDiceChooser } from '../../ui/components/OfferDiceChooser';
import type { GameModeId } from '@sdl/scene-format';

export interface OfferDiceTryDeps extends DiceTrySceneDeps {
  /** Dice codes from the offer's reward bundle. */
  offerDice: string[];
}

export class OfferDiceTryController extends DiceTryScene {
  protected override readonly sceneGameMode: GameModeId = 'offer-dice';
  private chooser: OfferDiceChooser | null = null;
  private offerDice: string[];

  constructor(deps: OfferDiceTryDeps) {
    super(deps);
    this.offerDice = deps.offerDice;
  }

  async start(): Promise<void> {
    if (this.active) return;
    this.active = true;

    const codes = this.offerDice.filter(Boolean);
    await this.preload(codes);
    if (!this.active) return;

    await this.buildScene();
    const slots = this.buildChooser(codes);
    this.buildCloseButton();
    this.spawnDice(slots);

    this.engine.start();
  }

  /** Build the chooser (only when there's a choice: 2+ dice). Returns the
   *  initial 3-die loadout. A single-die offer throws 3 of that die, no chooser. */
  private buildChooser(codes: string[]): string[] {
    if (codes.length <= 1) return [codes[0], codes[0], codes[0]];
    this.chooser = new OfferDiceChooser({
      thumbUrl: (id) => this.thumb(id),
      onChange: (slots) => this.spawnDice(slots),
    });
    this.chooser.setOffer(codes);
    this.chooser.show(); // auto-open expanded the first time
    return this.chooser.getSlots();
  }

  protected onDiceHandling(): void {
    this.chooser?.collapse();
  }

  protected disposeControls(): void {
    this.chooser?.destroy();
    this.chooser = null;
  }
}
